import CustomScrollbar from 'components/Common/CustomScrollbar';
import CommitteePublicationNewContent from 'components/Dashboard/CommitteePublication/CommitteePublicationNewContent';
import ConsultationNewContent from 'components/Dashboard/ConsultationsSection/ConsultationNewContent';
import useLoadContentResults from 'components/Dashboard/ContentControl/useLoadContentResults';
import DashboardTotalContributionsWidget from 'components/Dashboard/DashboardComponent/DashboardTotalContributionsWidget';
import UpcomingEventDashboardUpdate, {
  UpcomingEventDashboardUpdateTitle,
} from 'components/Dashboard/DashboardComponent/UpcomingEventDashboardUpdate';
import { regexSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import time from 'utils/time';
import useGetMatchingKeywordsListsForContent from 'components/Dashboard/hooks/useMatchingKeywordsListsForContent';
import KeyUpdateNewContent from 'components/Dashboard/KeyUpdates/KeyUpdateNewContent';
import LegislationNewContent from 'components/Dashboard/Legislation/LegislationNewContent';
import LibraryMaterialMainContent from 'components/Dashboard/LibraryMaterial/LibraryMaterialMainContent';
import ParliamentaryNewContent, {
  ParliamentaryRecordHeadingDate,
} from 'components/Dashboard/ParliamentaryRecord/ParliamentaryNewContent';
import useGetParliamentaryContributions from 'components/Dashboard/ParliamentaryRecord/useGetParliamentaryContributions';
import ParliamentaryLiveRemarksWidget from 'components/Dashboard/ParliamentLive/ParliamentaryLiveRemarksWidget';
import ParliamentLiveEventView from 'components/Dashboard/ParliamentLive/ParliamentLiveEventView';
import ChangeKeywordsCategory from 'components/Dashboard/utilities/changeKeywordsCategory';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import normalize from 'components/Dashboard/utilities/normalizeString';
import ReportsInfluenceContent from 'components/Reports/ui/ReportsInfluenceComponent';
import transformToPascalCase from 'components/Settings/Utilities/TransformToPascalCase';
import { store } from 'components/Store';
import { useContext, useEffect, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Portal } from 'react-portal';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { removeEmptyParagraphs } from 'utils/removeEmptyParagraphs';

const createURLProps = ({ url }) => {
  let path, propType, propId;
  if (!!url) {
    path = regexSectionName(url);
    propType = url?.split('/')?.[2];
    propId = isNaN(parseInt(propType)) ? url?.split('/')?.[3] : parseInt(propType);
  }
  if (url?.includes('/calendar')) {
    propId = url?.split('#event-')?.[1]?.replace('?topic-id=all', '');
    path = 'Calendar';
  }
  return { path, propType, propId };
};

const DashboardUpdateComponent = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan, team, keywordsLists } = state;

  const { enableLiveTranscription } = activePlan ?? {};

  const { updateState, transitionFinishes, resetEventParameter } = props ?? {};
  const { updatePanelState, setUpdatePanelState } = updateState ?? {};
  const { url, open, eventId, eventUrl, eventTitle, eventRemarks, eventSourceUrl, highlightUpdate, timesClicked } =
    updatePanelState ?? {};

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingEvent, setIsLoadingEvent] = useState(false);
  const [content, setContent] = useState({});
  const [eventState, setEventState] = useState({ hits: [], eventItem: null });
  const [showTooltip, setShowTooltip] = useState(false);
  const [dimentions, setDimentions] = useState({ top: 0, left: 0 });
  const [scrolling, setScrolling] = useState(false);
  const urlCondition = url && url !== 'noContent';
  const [localURL, setLocalURL] = useState(urlCondition ? new URL(import.meta.env.VITE_APP_APPURL + url) : null);
  useEffect(() => {
    if (urlCondition) {
      setLocalURL(new URL(import.meta.env.VITE_APP_APPURL + url));
    }
  }, [url]);

  const { top, left } = dimentions;

  const { getNewResult, highlightContent } = useLoadContentResults({});
  const { getMatchingKeywordsListsAndFeedback } = useGetMatchingKeywordsListsForContent();
  const { getTotalContributions } = useGetParliamentaryContributions({});
  const navigate = useNavigate();
  const location = useLocation();

  const officialReportPaths = [
    'scottish-official-report',
    'welsh-official-report',
    'senedd-committee-transcript',
    'hansard-content',
  ];

  const officialReportCondition = officialReportPaths.some((path) => localURL?.pathname?.includes(path));

  const contentContainer = useRef(null);
  const scrollbarRef = useRef(null);
  const titleRef = useRef(null);
  const hiddenRef = useRef(null);
  const generalContainer = useRef(null);
  const buttonRef = useRef(null);
  const buttonLiveRef = useRef(null);
  const containerButtons = useRef(null);

  const { eventItem, hits } = eventState;
  const createTitle = () => {
    if (!!eventTitle) {
      return eventTitle ?? '';
    } else {
      return content?.billTitle ?? content?.title ?? content?.subject;
    }
  };
  const titleText = createTitle();

  const resetTopicHandler = () => {
    const topicIdParam = getUrlParam('topic-id', new URL(import.meta.env.VITE_APP_APPURL + url));
    const topicExists =
      !keywordsLists.find((item) => item.id === (topicIdParam === 'all' ? null : parseInt(topicIdParam))) ||
      (!topicIdParam && !url?.includes('/calendar'));

    const resetTopic = () => {
      setLocalURL(
        new URL(
          `${import.meta.env.VITE_APP_APPURL}${localURL?.pathname}?${topicIdParam ? 'topic-id=none' : ''}${
            localURL?.hash
          }`
        )
      );
    };
    return { topicExists, resetTopic, topicIdParam };
  };

  const initialCall = useRef(null);
  initialCall.current = async (source) => {
    try {
      if (!!url) {
        setIsLoading(true);
        const { path, propId, propType } = createURLProps({ url }) ?? {};
        let results = await getNewResult({
          source,
          nameSection: transformToPascalCase(path),
          path,
          propType,
          propId,
        });
        if (!!results) {
          let newResult = { ...results };
          const { objectId } = newResult;
          let newData = { ...newResult };
          const { feedback, keywordsFromLists, keywordsListsIds } = await getMatchingKeywordsListsAndFeedback({
            objectId,
          });
          newData = { ...newData, feedback, keywordsFromLists, keywordsListsIds };
          newResult = { ...newData };
          const { topicExists, resetTopic, topicIdParam } = resetTopicHandler();
          if (topicExists) {
            resetTopic();
          } else {
            const topicIsValid = keywordsListsIds?.includes(parseInt(topicIdParam));
            const topicIdToUse = topicIsValid ? topicIdParam : null;
            if (!topicIsValid && localURL?.pathname && topicIdParam !== 'all') {
              setLocalURL(
                new URL(`${import.meta.env.VITE_APP_APPURL}${localURL?.pathname}?topic-id=none${localURL?.hash}`)
              );
            }
            const list =
              topicIdParam === 'all' || url?.includes('/calendar')
                ? keywordsLists.find((item) => item.id === null)
                : keywordsLists.find((item) => item.id === parseInt(topicIdToUse));

            if (list && (topicIsValid || url?.includes('/calendar'))) {
              const contentHightlighted = await highlightContent({
                content: newData,
                source,
                forceHighlight: true,
                bodyProperty:
                  officialReportCondition && !localURL?.pathname?.includes('hansard-content')
                    ? 'content'
                    : url?.includes('/calendar')
                      ? 'description'
                      : null,
                titleProperty: url?.includes('/calendar') ? 'title' : null,
                queryHighlight: ChangeKeywordsCategory(list.keywords),
                andKeywords: list.andKeywords,
              });
              newResult = { ...contentHightlighted };
              if (officialReportCondition && !localURL?.pathname?.includes('senedd-committee-transcript')) {
                const { canonicalUrl } = newResult;
                let totalContributions = await getTotalContributions({
                  contentUrl: canonicalUrl,
                  type: propType,
                  source,
                  localURL,
                });
                if (totalContributions?.hits?.length > 0) {
                  newResult = { ...newResult, totalContributions: totalContributions };
                }
              }
            }
          }
          setContent(newResult);
          setIsLoading(false);
        }
      } else if (!!eventId) {
        setContent({});
        setEventState({ hits: [], eventItem: null });
        setIsLoading(true);
        setIsLoadingEvent(true);
        setTimeout(() => {
          const searchParams = new URLSearchParams(location?.search);
          searchParams.delete('in');
          navigate({ search: searchParams?.toString() });
          setIsLoading(false);
          setIsLoadingEvent(false);
        }, 200);
      } else {
        setIsLoading(false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Update call cancelled by the user');
    };
  }, [url, eventId, localURL?.pathname]);

  useEffect(() => {
    if (titleRef.current && hiddenRef.current) {
      const visibleWidth = titleRef.current.offsetWidth;
      const fullWidth = hiddenRef.current.offsetWidth;
      setShowTooltip(fullWidth > visibleWidth);
    }
  }, [titleText, isLoading]);

  useEffect(() => {
    if (generalContainer?.current && containerButtons?.current && open && transitionFinishes) {
      const updateDimentions = () => {
        const { top, left, width } = generalContainer?.current?.getBoundingClientRect() ?? {};
        const buttonsWidth = containerButtons?.current?.getBoundingClientRect()?.width;
        setDimentions({ top: top - 18, left: left + width + 17 - buttonsWidth });
      };
      setTimeout(() => {
        updateDimentions();
      }, 50);
      window.addEventListener('resize', updateDimentions);
      return () => {
        window.removeEventListener('resize', updateDimentions);
      };
    }
  }, [generalContainer?.current, containerButtons?.current, open, transitionFinishes, eventSourceUrl, url]);

  useEffect(() => {
    let timeoutId;
    if (highlightUpdate) {
      timeoutId = setTimeout(() => {
        setUpdatePanelState({ ...updatePanelState, highlightUpdate: false });
      }, 3000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [highlightUpdate, open]);

  return (
    <>
      <div
        className={`w-100 box-top-rounded h-100 bg-main-white dashboard-root-container ${
          highlightUpdate ? 'dashboard-update-main-container-active' : ''
        }`}
        ref={generalContainer}
        key={`highlight-${timesClicked?.[url]}`}
      >
        {url === 'noContent' ? (
          <>
            <div
              className='h-100 flex-column flex-centered justify-content-center mx-auto text-center'
              style={{ marginTop: '-70px' }}
            >
              <p className='light-50-text'>This update has been removed from PolicyMogul</p>
            </div>
          </>
        ) : (
          <>
            <div
              className='flex-centered border-bottom specific-x-padding-28 py-3 justify-content-between'
              style={{ minHeight: '90px' }}
            >
              {isLoading && !eventId ? (
                <SkeletonTheme baseColor='#f2f2f2'>
                  <div>
                    <Skeleton width={'200px'} height={19} />
                  </div>
                </SkeletonTheme>
              ) : (
                <>
                  {url?.includes('/calendar') ? (
                    <UpcomingEventDashboardUpdateTitle content={content} />
                  ) : (
                    <div>
                      <h3
                        className='main-title mb-0 hidden-lines hidden-one-line line-height-1-4'
                        ref={titleRef}
                        data-tooltip-id='general-tooltip'
                        data-tooltip-content={showTooltip ? titleText : ''}
                        data-tooltip-class-name='pricing-feature-tooltip feed-tooltip billing-tooltip'
                      >
                        {titleText}
                      </h3>
                      <span
                        ref={hiddenRef}
                        className='position-absolute main-title nowrap-item'
                        style={{
                          visibility: 'hidden',
                          top: 0,
                        }}
                      >
                        {titleText}
                      </span>
                      {(() => {
                        const props = {
                          currentNew: content,
                        };
                        const { path } = createURLProps({ url });
                        switch (path) {
                          case 'Parliamentary record':
                            return (
                              <div className='specific-top-margin-4' key={`${normalize(path)}-content`}>
                                <ParliamentaryRecordHeadingDate {...props} />
                              </div>
                            );
                          default:
                            return '';
                        }
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
            {isLoading || isLoadingEvent ? (
              <div
                className={'specific-x-padding-22 specific-x-padding-lg-29 specific-y-padding-32 h-100 bg-main-white'}
              >
                <SkeletonTheme baseColor='#f2f2f2'>
                  {Array(9)
                    .fill()
                    .map((item, index) => (
                      <div key={index} className='mb-3'>
                        <Skeleton count={4} height={19} width={`100%`} />
                        <Skeleton height={19} width={`45%`} />
                      </div>
                    ))}
                </SkeletonTheme>
              </div>
            ) : (
              <>
                {!!eventId ? (
                  <div ref={contentContainer} className='dashboard-update-container h-100'>
                    <div className='dashboard-root-container'>
                      {enableLiveTranscription ? (
                        <>
                          {!!eventItem && hits?.length > 1 && getUrlParam('in') && (
                            <div className='specific-x-padding-28 pt-3'>
                              <div className='border rounded bg-main-white'>
                                <ParliamentaryLiveRemarksWidget hits={hits} />
                              </div>
                            </div>
                          )}
                          <div className='flex-grow-1'>
                            <ParliamentLiveEventView dashboard={true} setEventState={setEventState} eventId={eventId} />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className='specific-x-padding-22 specific-x-padding-lg-30 py-4'>
                            <div className='simple-box rounded specific-x-padding-28 specific-y-padding-28'>
                              <div className='rounded bg-main-grey-15 px-3 py-1 flex-centered light-60-text mb-4 border'>
                                <span className='flex-grow-1'>
                                  {eventRemarks} relevant remark{eventRemarks === 1 ? '' : 's'}
                                </span>
                                <div className='border-right specific-right-padding-10 specific-right-margin-10'>
                                  <span className='icon-arrow-back paragraph-p4 light-30-text specific-right-margin-10' />
                                  <span className='icon-arrow-next paragraph-p4 light-30-text' />
                                </div>
                                <span className='icon-padlock' />
                              </div>
                              <p className='text-center light-80-text mb-0'>
                                Parliament live is available on the Mogul and Enterprise plans. Please{' '}
                                <Link className='simple-link-text' to='/pricing'>
                                  upgrade your subscription
                                </Link>{' '}
                                to see relevant remarks for {team?.teamName}
                              </p>
                            </div>
                            <div className='my-4 text-center position-relative group-separator'>
                              <hr className='position-absolute border-top top-0 w-100' />
                              <span className='light-70-text bg-main-white py-1 px-3 d-inline-block z-10 position-relative or-separator'>
                                or
                              </span>
                            </div>
                            <div className='text-center pt-2'>
                              <a
                                href={eventSourceUrl}
                                className='action-button px-4 general-button py-2 rounded-pill'
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                <span className='icon-single-play mr-2' />
                                Watch this event live
                              </a>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {content?.embargoed && (
                      <div className='specific-x-padding-22 pt-3'>
                        <p className='my-2 text-red-warning border px-2 d-flex align-items-center justify-content-center specific-y-padding-9'>
                          <span className='icon-clock-thin font-weight-bold' />
                          <span className='specific-left-padding-10'>
                            Embargoed{' '}
                            {!!content?.embargoedUntil
                              ? `until ${time(content?.embargoedUntil).formatEmbargoedDate()}`
                              : ''}
                          </span>
                        </p>
                      </div>
                    )}
                    <CustomScrollbar
                      ref={scrollbarRef}
                      className='simple-scrollbar'
                      maximalThumbYSize={100}
                      onScrollStart={() => {
                        setScrolling(true);
                      }}
                      onScrollStop={() => {
                        setScrolling(false);
                      }}
                    >
                      {url?.includes('/calendar') ? (
                        <UpcomingEventDashboardUpdate
                          content={content}
                          scrolling={scrolling}
                          scrollbarRef={scrollbarRef}
                        />
                      ) : (
                        <div ref={contentContainer} className='dashboard-update-container'>
                          {content?.totalContributions?.hits && (
                            <div className='position-sticky top-0 right-0 z-100'>
                              <div className='specific-x-padding-16 pt-3 bg-main-white bottom-shadow-fade'>
                                <div className='border rounded bg-main-white'>
                                  <DashboardTotalContributionsWidget
                                    hits={content?.totalContributions?.hits}
                                    localURL={localURL}
                                    setLocalURL={setLocalURL}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          <div className='specific-top-padding-8'></div>
                          {(() => {
                            const currentNew = content;
                            const contributionId = content?.contributionId;
                            const typeOfContent = content?.objectType;
                            // Function to remove empty paragraphs
                            const props = {
                              currentNew: {
                                ...currentNew,
                                body: removeEmptyParagraphs(currentNew?.body),
                                content: removeEmptyParagraphs(currentNew?.content),
                                htmlContent: removeEmptyParagraphs(currentNew?.htmlContent),
                              },
                              contributionId,
                              contentContainer,
                              hideSummary: true,
                              shareButtonWidget: false,
                              scrollbarRef,
                              scrolling,
                            };
                            switch (typeOfContent) {
                              case 'Legislation':
                                return <LegislationNewContent {...props} key={`${typeOfContent}-content`} />;
                              case 'ParliamentaryRecord':
                              case 'HansardContent':
                              case 'CountrySpecificParliamentaryRecord':
                              case 'SeneddCommitteeTranscript':
                                return (
                                  <ParliamentaryNewContent
                                    {...props}
                                    key={`${typeOfContent}-content`}
                                    dashboardUpdate={true}
                                    localURL={localURL}
                                  />
                                );
                              case 'CommonsLibraryArticle':
                                return <LibraryMaterialMainContent {...props} key={`${typeOfContent}-content`} />;
                              case 'KeyUpdate':
                                return <KeyUpdateNewContent {...props} key={`${typeOfContent}-content`} />;
                              case 'Consultation':
                                return <ConsultationNewContent {...props} key={`${typeOfContent}-content`} />;
                              case 'UserContent':
                                return <ReportsInfluenceContent {...props} key={`${typeOfContent}-content`} />;
                              case 'CommitteePublication':
                                return <CommitteePublicationNewContent {...props} key={`${typeOfContent}-content`} />;
                              default:
                                return '';
                            }
                          })()}
                        </div>
                      )}
                    </CustomScrollbar>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {open && (
        <Portal>
          <div
            ref={containerButtons}
            className='flex-centered position-fixed'
            style={{ top: `${top}px`, left: `${left}px` }}
          >
            <a
              ref={buttonLiveRef}
              href={`${eventSourceUrl}`}
              className='reset-link reset-link-color flex-centered justify-content-center text-center general-button hovered-button circle-button-36 border specific-right-margin-6 text-main-blue bg-main-white text-center'
              target={'_blank'}
              rel='noopener noreferrer'
              data-tooltip-id={'general-tooltip'}
              data-tooltip-content={'Watch live'}
              style={{ visibility: !!eventSourceUrl && enableLiveTranscription ? 'visible' : 'hidden' }}
              onClick={() => buttonLiveRef?.current?.blur()}
            >
              <span className='icon-single-play specific-left-margin-4 paragraph-p3' />
            </a>
            {!url?.includes('/calendar') && (
              <a
                ref={buttonRef}
                href={`${window.location.origin}${url ?? eventUrl}`}
                className='reset-link reset-link-color flex-centered justify-content-center text-center general-button hovered-button circle-button-36 border specific-right-margin-6 text-main-blue bg-main-white'
                target={'_blank'}
                rel='noopener noreferrer'
                data-tooltip-id={'general-tooltip'}
                data-tooltip-content={'Open in a new tab'}
                style={{ visibility: !!eventId && !enableLiveTranscription ? 'hidden' : 'visible' }}
                onClick={() => buttonRef.current.blur()}
              >
                <span className='icon-external-bold' />
              </a>
            )}
            <button
              className='general-button circle-button-34 bg-main-blue text-white'
              data-tooltip-id={'general-tooltip'}
              data-tooltip-content={'Close panel'}
              onClick={() => {
                setUpdatePanelState({ open: false, url: null });
                setContent({});
                setEventState({ hits: [], eventItem: null });
                resetEventParameter();
              }}
            >
              <span className='icon-close paragraph-p5' />
            </button>
          </div>
        </Portal>
      )}
    </>
  );
};

export { createURLProps };
export default DashboardUpdateComponent;
